import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
import { DISPLAY_KEY_PATTERN, ENTITLEMENT_GROUPS, ENTITLEMENT_KINDS, entitlementKeySchema, } from "./entitlements.js";
import { platformPlanListSchema } from "./platform-ops.js";
/**
 * PL-03 (docs/specs/plan-catalogue.md): the Super Admin keeps the plans and the feature list without a deploy —
 * create, duplicate and edit plans, change many values at once (with an impact preview), and edit the feature list.
 */
const reason = z.string().trim().min(1).max(500);
const platform = { platformAdmin: true };
const tag = { tag: "platform-admin" };
const planKeySchema = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/);
const planParams = z.object({ key: planKeySchema });
export const entitlementGroupSchema = z.enum(ENTITLEMENT_GROUPS);
/** A code key or a Super Admin's `display.*` line. */
export const catalogueKeySchema = z.union([entitlementKeySchema, z.string().regex(DISPLAY_KEY_PATTERN)]);
const name = z.string().trim().min(1).max(80);
const description = z.string().trim().max(300);
const unit = z.string().trim().max(30);
const note = z.string().trim().max(80);
// Feature list --------------------------------------------------------------------------------------------------
export const entitlementDefinitionSchema = z.object({
    key: z.string(),
    kind: z.enum(ENTITLEMENT_KINDS),
    group: entitlementGroupSchema,
    nameEn: z.string(),
    nameBn: z.string(),
    descriptionEn: z.string(),
    descriptionBn: z.string(),
    unitEn: z.string(),
    unitBn: z.string(),
    sortOrder: z.number().int(),
    showOnPricing: z.boolean(),
    status: z.enum(["active", "archived"]),
    /** Whether the code checks this key yet; display lines are never checked. */
    enforced: z.boolean(),
    version: z.number().int(),
});
export const entitlementDefinitionListSchema = z.object({ data: z.array(entitlementDefinitionSchema) });
export const listEntitlementDefinitions = defineEndpoint({
    ...tag,
    name: "listEntitlementDefinitions",
    method: "GET",
    path: "/v1/platform/entitlement-definitions",
    access: platform,
    summary: "The feature list: every key and display line with its words, group and order",
    status: 200,
    response: entitlementDefinitionListSchema,
});
export const createEntitlementDefinitionSchema = z.object({
    key: z.string().regex(DISPLAY_KEY_PATTERN),
    group: entitlementGroupSchema,
    nameEn: name,
    nameBn: name,
    descriptionEn: description.default(""),
    descriptionBn: description.default(""),
    showOnPricing: z.boolean().default(true),
    reason,
});
export const createEntitlementDefinition = defineEndpoint({
    ...tag,
    name: "createEntitlementDefinition",
    method: "POST",
    path: "/v1/platform/entitlement-definitions",
    access: platform,
    summary: "Add a display line (shown on plans, never checked by the code)",
    body: createEntitlementDefinitionSchema,
    status: 201,
    response: entitlementDefinitionListSchema,
});
export const updateEntitlementDefinitionSchema = z.object({
    group: entitlementGroupSchema.optional(),
    nameEn: name.optional(),
    nameBn: name.optional(),
    descriptionEn: description.optional(),
    descriptionBn: description.optional(),
    unitEn: unit.optional(),
    unitBn: unit.optional(),
    showOnPricing: z.boolean().optional(),
    /** Display lines only. */
    status: z.enum(["active", "archived"]).optional(),
    /** The line's `version`; 0 for a code key still showing its built-in words. */
    version: z.number().int().min(0),
    reason,
});
export const updateEntitlementDefinition = defineEndpoint({
    ...tag,
    name: "updateEntitlementDefinition",
    method: "PATCH",
    path: "/v1/platform/entitlement-definitions/{key}",
    access: platform,
    summary: "Change a line's words, group or pricing visibility (archive display lines)",
    params: z.object({ key: catalogueKeySchema }),
    body: updateEntitlementDefinitionSchema,
    status: 200,
    response: entitlementDefinitionListSchema,
});
export const orderEntitlementDefinitionsSchema = z.object({
    group: entitlementGroupSchema,
    keys: z.array(catalogueKeySchema).min(1).max(100),
    reason,
});
export const orderEntitlementDefinitions = defineEndpoint({
    ...tag,
    name: "orderEntitlementDefinitions",
    method: "PUT",
    path: "/v1/platform/entitlement-definitions/order",
    access: platform,
    summary: "Put a group's lines in this order",
    body: orderEntitlementDefinitionsSchema,
    status: 200,
    response: entitlementDefinitionListSchema,
});
// Plans ---------------------------------------------------------------------------------------------------------
export const createPlatformPlanSchema = z.object({
    key: planKeySchema,
    nameEn: z.string().trim().min(1).max(100),
    nameBn: z.string().trim().min(1).max(100),
    /** Copy values, notes, texts and prices of this plan. */
    copyFrom: planKeySchema.optional(),
    reason,
});
export const createPlatformPlan = defineEndpoint({
    ...tag,
    name: "createPlatformPlan",
    method: "POST",
    path: "/v1/platform/plans",
    access: platform,
    summary: "Create a plan (or duplicate one); it starts hidden from the pricing page",
    body: createPlatformPlanSchema,
    status: 201,
    errors: ["CONFLICT"],
    response: platformPlanListSchema,
});
const tagline = z.string().trim().max(120);
const badge = z.string().trim().max(30);
export const updatePlatformPlanSchema = z.object({
    nameEn: z.string().trim().min(1).max(100).optional(),
    nameBn: z.string().trim().min(1).max(100).optional(),
    taglineEn: tagline.optional(),
    taglineBn: tagline.optional(),
    badgeEn: badge.optional(),
    badgeBn: badge.optional(),
    isHighlighted: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    isPublic: z.boolean().optional(),
    status: z.enum(["active", "retired"]).optional(),
    /** Only `true`: making another plan the default clears this one. */
    isDefault: z.literal(true).optional(),
    trialDays: z.number().int().min(1).max(90).nullable().optional(),
    version: z.number().int().min(1),
    reason,
});
export const updatePlatformPlan = defineEndpoint({
    ...tag,
    name: "updatePlatformPlan",
    method: "PATCH",
    path: "/v1/platform/plans/{key}",
    access: platform,
    summary: "Change a plan's words, order, visibility, status or default",
    params: planParams,
    body: updatePlatformPlanSchema,
    status: 200,
    errors: ["CONFLICT", "BUSINESS_RULE_VIOLATION"],
    response: platformPlanListSchema,
});
/** A value as the Super Admin sets it: a number (null = unlimited) for limits/credits, on/off otherwise. */
export const planCellValueSchema = z.union([z.number().int().min(0), z.boolean()]).nullable();
export const planValueChangeSchema = z.object({
    entitlement: catalogueKeySchema,
    value: planCellValueSchema,
    noteEn: note.optional(),
    noteBn: note.optional(),
});
export const PLAN_CHANGE_MODES = ["keep_existing", "apply_to_all"];
export const setPlanValuesSchema = z.object({
    values: z.array(planValueChangeSchema).min(1).max(100),
    /** `keep_existing` (default): businesses on the plan keep what gets smaller. */
    mode: z.enum(PLAN_CHANGE_MODES).default("keep_existing"),
    reason,
});
export const setPlanValues = defineEndpoint({
    ...tag,
    name: "setPlanValues",
    method: "PUT",
    path: "/v1/platform/plans/{key}/values",
    access: platform,
    summary: "Change many values of a plan at once",
    params: planParams,
    body: setPlanValuesSchema,
    status: 200,
    response: platformPlanListSchema,
});
export const previewPlanValuesSchema = z.object({
    values: z
        .array(planValueChangeSchema.pick({ entitlement: true, value: true }))
        .min(1)
        .max(100),
});
export const planValuesImpactSchema = z.object({
    /** Businesses with this plan (paid or assigned). */
    businesses: z.number().int(),
    /** Businesses on the default plan without one of their own (trials); 0 unless this is the default plan. */
    trials: z.number().int(),
    changes: z.array(z.object({
        key: z.string(),
        from: planCellValueSchema,
        to: planCellValueSchema,
        /** The value gets smaller for businesses (a lower limit, a feature off). */
        smaller: z.boolean(),
        /** Businesses already above the new limit; null when not a limit or too many businesses to count. */
        overLimit: z.object({ businesses: z.number().int(), trials: z.number().int() }).nullable(),
    })),
});
export const previewPlanValues = defineEndpoint({
    ...tag,
    name: "previewPlanValues",
    method: "POST",
    path: "/v1/platform/plans/{key}/values/preview",
    access: platform,
    summary: "What a change of values would do, without changing anything",
    params: planParams,
    body: previewPlanValuesSchema,
    status: 200,
    response: planValuesImpactSchema,
});
// Kept terms (PL-05) --------------------------------------------------------------------------------------------
export const tenantPlanTermSchema = z.object({
    planKey: z.string(),
    key: z.string(),
    value: planCellValueSchema,
    reason: z.string(),
    createdAt: z.iso.datetime(),
});
export const tenantPlanTermListSchema = z.object({ data: z.array(tenantPlanTermSchema) });
const tenantParams = z.object({ id: z.uuid() });
export const listTenantPlanTerms = defineEndpoint({
    ...tag,
    name: "listTenantPlanTerms",
    method: "GET",
    path: "/v1/platform/tenants/{id}/plan-terms",
    access: platform,
    summary: "Values a business kept after its plan was made smaller",
    params: tenantParams,
    status: 200,
    response: tenantPlanTermListSchema,
});
export const clearTenantPlanTerms = defineEndpoint({
    ...tag,
    name: "clearTenantPlanTerms",
    method: "POST",
    path: "/v1/platform/tenants/{id}/plan-terms/clear",
    access: platform,
    summary: "Move a business to its plan's current values",
    params: tenantParams,
    body: z.object({ reason }),
    status: 200,
    response: tenantPlanTermListSchema,
});
//# sourceMappingURL=plan-catalogue.js.map